import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Card,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { GreeningAnalysis } from '../../services/greeningApi';
import { GREENING_AGENT_TITLE_PREFIX, GREENING_ANALYSIS_QUESTIONS } from './constants';
import { GreeningAgentSignature } from './GreeningAgentSignature';
import { GreeningDisclaimer } from './GreeningDisclaimer';
import { GreeningBrandLabel } from './GreeningBrandLabel';
import { GreeningIcon } from './GreeningIcon';
import { GreeningProcessingStatus } from './GreeningProcessingStatus';
import { greeningReadingShade, indexAtRatio, scrollReadingTarget } from './greeningReadingMark';

interface Props {
  analysis?: GreeningAnalysis;
  loading?: boolean;
  /** Remove margem superior quando o card é o primeiro bloco da aba. */
  compactTop?: boolean;
  /** Seção que a leitura está falando agora. */
  readingSection?: string | null;
  /** Quanto dessa seção já foi falado, de 0 a 1. */
  readingRatio?: number;
}

const SPOKEN_LIST_HEADER: Record<string, string> = {
  controls: 'Controles sugeridos',
  demands: 'Demandas',
  sanctions: 'Sanções e riscos',
};

/** Frases na ordem da tela. O texto, com espaços e quebras, continua o mesmo. */
function displaySentences(text: string): string[] {
  const parts: string[] = [];
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    const ch = text[index];
    const punctuation = ch === '.' || ch === '!' || ch === '?';
    const boundary = ch === '\n' || (punctuation && (!text[index + 1] || /\s/.test(text[index + 1])));
    if (!boundary) continue;
    if (ch === '.') {
      const word = text.slice(start, index).match(/(\S+)$/)?.[1] ?? '';
      if (/^(?:art|arts|n|no|nº)$/i.test(word) || /^\d{1,3}$/.test(word)) continue;
    }
    let end = index + 1;
    if (ch !== '\n') {
      while (end < text.length && /[ \t]/.test(text[end])) end += 1;
    }
    if (end > start) parts.push(text.slice(start, end));
    start = end;
    index = end - 1;
  }
  if (start < text.length) parts.push(text.slice(start));
  return parts.length ? parts : text ? [text] : [];
}

export function GreeningAnalysisPanel({
  analysis,
  loading,
  compactTop,
  readingSection,
  readingRatio = 0,
}: Props) {
  const theme = useTheme();
  const active =
    analysis?.status === 'pending' ||
    analysis?.status === 'processing' ||
    analysis?.status === 'retrying';
  const fiveWhysActive =
    analysis?.five_whys_status === 'pending' || analysis?.five_whys_status === 'processing';
  const isError =
    analysis?.status === 'failed' ||
    analysis?.status === 'insufficient_context' ||
    analysis?.status === 'blocked_by_security';
  const fiveWhys = analysis?.five_whys;
  const fiveWhysReady = analysis?.five_whys_status === 'completed';
  const hasQuestions =
    fiveWhysReady &&
    fiveWhys &&
    GREENING_ANALYSIS_QUESTIONS.some(({ key }) => fiveWhys[key]?.trim());
  const hasBody =
    Boolean(analysis?.analysis_text) ||
    Boolean(analysis?.controls?.length) ||
    Boolean(analysis?.demands?.length) ||
    Boolean(analysis?.sanctions?.length) ||
    hasQuestions;

  const [expanded, setExpanded] = useState<string | false>(false);
  const shownMark = useRef<string | null>(null);
  const analysisParts = displaySentences(analysis?.analysis_text || '');
  const analysisIndex = readingSection === 'analysis' ? indexAtRatio(analysisParts, readingRatio) : -1;
  const listParts = (section: string, items: string[]) => [SPOKEN_LIST_HEADER[section] || section, ...items];
  const listIndex = (section: string, items: string[]) => (
    readingSection === section ? indexAtRatio(listParts(section, items), readingRatio) : -1
  );
  const whyParts = (label: string, text: string) => [label, ...displaySentences(text)];
  const whyIndex = (label: string, text: string, key: string) => (
    readingSection === key ? indexAtRatio(whyParts(label, text), readingRatio) : -1
  );

  const markId = (() => {
    if (!readingSection) return null;
    if (readingSection === 'analysis') {
      return analysisIndex >= 0 ? `greening-line-analysis-${analysisIndex}` : 'greening-section-analysis';
    }
    if (readingSection === 'controls' || readingSection === 'demands' || readingSection === 'sanctions') {
      const items = analysis?.[readingSection] || [];
      const index = listIndex(readingSection, items);
      if (index <= 0) return `greening-section-${readingSection}`;
      return `greening-item-${readingSection}-${index - 1}`;
    }
    const question = GREENING_ANALYSIS_QUESTIONS.find(({ key }) => key === readingSection);
    const answer = question ? fiveWhys?.[question.key]?.trim() || '' : '';
    const index = question ? whyIndex(question.label, answer, question.key) : -1;
    if (!question || index <= 0) return `greening-section-${readingSection}`;
    return `greening-line-${readingSection}-${index - 1}`;
  })();

  useEffect(() => {
    if (!readingSection || !markId) {
      shownMark.current = null;
      return undefined;
    }
    const toggleKeys = new Set<string>(GREENING_ANALYSIS_QUESTIONS.map(({ key }) => key));
    if (toggleKeys.has(readingSection)) setExpanded(readingSection);
    if (shownMark.current === markId) return undefined;
    shownMark.current = markId;
    const timer = window.setTimeout(() => scrollReadingTarget(markId), 80);
    return () => window.clearTimeout(timer);
  }, [markId, readingSection]);

  const readingShade = (active: boolean) => greeningReadingShade(theme, active);

  const accordionSx = {
    bgcolor: alpha(theme.palette.success.main, 0.06),
    border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
    borderRadius: '6px !important',
    mb: 0.75,
    '&:before': { display: 'none' },
    boxShadow: 'none',
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mt: compactTop ? 0 : 2,
        p: 2,
        borderColor: alpha(theme.palette.success.main, 0.5),
        bgcolor: alpha(theme.palette.success.main, 0.04),
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <GreeningIcon sx={{ fontSize: 28, color: 'success.main' }} />
        <Typography variant="subtitle1" fontWeight={700} color="success.dark">
          {GREENING_AGENT_TITLE_PREFIX} <GreeningBrandLabel fontWeight={700} />
        </Typography>
      </Box>

      {(loading || active || isError) && (
        <GreeningProcessingStatus
          status={analysis?.status}
          userMessage={analysis?.user_message || analysis?.error_message || undefined}
        />
      )}

      {analysis?.status === 'completed' && fiveWhysActive && (
        <GreeningProcessingStatus
          status={analysis?.five_whys_status as GreeningAnalysis['status']}
          userMessage={analysis?.five_whys_message || 'Aprofundando os 5 Porquês…'}
        />
      )}

      {analysis?.analysis_text && (
        <Typography
          id="greening-section-analysis"
          component="div"
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', mt: 1, px: 1, py: 0.75 }}
        >
          {analysisParts.map((part, index) => (
            <Box
              key={`analysis-${index}`}
              id={`greening-line-analysis-${index}`}
              component="span"
              sx={readingShade(index === analysisIndex)}
            >
              {part}
            </Box>
          ))}
        </Typography>
      )}

      {analysis?.controls && analysis.controls.length > 0 && (
        <Box id="greening-section-controls" mt={2} sx={{ px: 1, py: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={readingShade(listIndex('controls', analysis.controls) === 0)}
          >
            Controles sugeridos
          </Typography>
          <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
            {analysis.controls.map((item, index) => (
              <li key={item} id={`greening-item-controls-${index}`}>
                <Typography variant="body2" sx={readingShade(listIndex('controls', analysis.controls || []) === index + 1)}>
                  {item}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}

      {analysis?.demands && analysis.demands.length > 0 && (
        <Box id="greening-section-demands" mt={1.5} sx={{ px: 1, py: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={readingShade(listIndex('demands', analysis.demands) === 0)}
          >
            Demandas operacionais / documentais
          </Typography>
          <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
            {analysis.demands.map((item, index) => (
              <li key={item} id={`greening-item-demands-${index}`}>
                <Typography variant="body2" sx={readingShade(listIndex('demands', analysis.demands || []) === index + 1)}>
                  {item}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}

      {analysis?.sanctions && analysis.sanctions.length > 0 && (
        <Box id="greening-section-sanctions" mt={1.5} sx={{ px: 1, py: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={readingShade(listIndex('sanctions', analysis.sanctions) === 0)}
          >
            Sanções, riscos e consequências
          </Typography>
          <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
            {analysis.sanctions.map((item, index) => (
              <li key={item} id={`greening-item-sanctions-${index}`}>
                <Typography variant="body2" sx={readingShade(listIndex('sanctions', analysis.sanctions || []) === index + 1)}>
                  {item}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}

      {hasQuestions && (
        <Box sx={{ mt: 2.5 }}>
          <Divider sx={{ mb: 1.5, borderColor: alpha(theme.palette.success.main, 0.2) }} />
          {GREENING_ANALYSIS_QUESTIONS.map(({ key, label }) => {
            const text = fiveWhys?.[key]?.trim();
            if (!text) return null;
            const sentences = displaySentences(text);
            const active = whyIndex(label, text, key);
            return (
              <Accordion
                id={`greening-section-${key}`}
                key={key}
                expanded={expanded === key}
                onChange={(_, isExp) => setExpanded(isExp ? key : false)}
                disableGutters
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'success.dark' }} />}>
                  <Typography variant="body2" fontWeight={600} color="success.dark" sx={readingShade(active === 0)}>
                    {label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                    {sentences.map((sentence, index) => (
                      <Box
                        key={`${key}-${index}`}
                        id={`greening-line-${key}-${index}`}
                        component="span"
                        sx={readingShade(active === index + 1)}
                      >
                        {sentence}
                      </Box>
                    ))}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {hasBody && (
        <Box
          sx={{
            mt: 2.5,
            borderTop: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
            pt: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic', mb: 0.75 }}>
            <GreeningDisclaimer />
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
            <GreeningAgentSignature />
          </Typography>
        </Box>
      )}
    </Card>
  );
}

export default GreeningAnalysisPanel;
